import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  adGroupActiveAriaLabel,
  resolveAdGroupActiveSwitchMode,
} from '@/utils/adGroupActiveState';

type AdGroupActiveSwitchProps = {
  adGroupName: string;
  state: string | null | undefined;
  pending?: boolean;
  disabled?: boolean;
  onToggle: () => void;
};

export function AdGroupActiveSwitch({
  adGroupName,
  state,
  pending = false,
  disabled = false,
  onToggle,
}: AdGroupActiveSwitchProps) {
  const mode = resolveAdGroupActiveSwitchMode(state);

  if (mode.kind === 'unsupported') {
    return (
      <span
        className="inline-flex items-center justify-center text-xs text-foreground-subtle"
        title={`Active status unavailable (${mode.label})`}
      >
        {mode.label}
      </span>
    );
  }

  const isDisabled = disabled || pending;

  return (
    <span className="inline-flex items-center justify-center gap-1.5">
      <button
        type="button"
        role="switch"
        aria-checked={mode.checked}
        aria-label={adGroupActiveAriaLabel(adGroupName, mode)}
        aria-busy={pending || undefined}
        disabled={isDisabled}
        onClick={onToggle}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          mode.checked ? 'bg-brand' : 'bg-border',
          isDisabled && 'cursor-not-allowed opacity-60'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-surface shadow transition-transform',
            mode.checked && 'translate-x-4'
          )}
        />
      </button>
      {pending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground-subtle" aria-hidden />
      ) : null}
    </span>
  );
}
